import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { useCreateRecoveryScenario, useUpdateRecoveryScenario } from '../hooks'
import type { RecoveryScenario, ScenarioDependency, ScenarioTemplateKey } from '../api'
import { ScenarioDependsOn } from './ScenarioDependsOn'
import { ScenarioPartsPlan, applyTemplate, planProblem, type PlanValue } from './ScenarioPartsPlan'
import { useEstatePartOptions, useEstateParts, useScenarioTemplates } from '../estate-parts'

/**
 * When the scenario is to be used (Andrew, 13 Oct 2026: "Each scenario needs more information on
 * when the scenario is to be used"): "Use when", "Do not use when", what it depends on, where each
 * part of the estate goes, and whether it is production. There is no single "Recovers to"
 * (Andrew, 15 Oct 2026: "Remove the settings for recovers to because it's too prescriptive").
 */
const scenarioSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    description: z.string().optional(),
    useCase: z.string().optional(),
    whenNotToUse: z.string().optional(),
    isDefault: z.boolean(),
    isProduction: z.boolean(),
})

type ScenarioFormData = z.infer<typeof scenarioSchema>

interface ScenarioFormDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    scenario: RecoveryScenario | null
}

export function ScenarioFormDialog({ open, onOpenChange, scenario }: ScenarioFormDialogProps) {
    const { t } = useTranslation()
    const isEditing = !!scenario
    const createScenario = useCreateRecoveryScenario()
    const updateScenario = useUpdateRecoveryScenario()

    // When editing the scenario that is currently the default, the Default control
    // is locked on — the default must be reassigned by marking another scenario default.
    const isEditingCurrentDefault = isEditing && scenario?.isDefault === true
    // Production moves the same way: by marking another scenario production.
    const isEditingCurrentProduction = isEditing && scenario?.isProduction === true

    const form = useForm<ScenarioFormData>({
        resolver: zodResolver(scenarioSchema),
        mode: 'onChange',
        defaultValues: {
            name: '',
            description: '',
            useCase: '',
            whenNotToUse: '',
            isDefault: false,
            isProduction: false,
        },
    })


    // Not a form field: a list built by clicking, sent whole with the rest.
    const [dependsOn, setDependsOn] = useState<ScenarioDependency[]>([])
    useEffect(() => { if (open) setDependsOn(scenario?.dependsOn ?? []) }, [open, scenario])

    // Where each part of the estate goes (ScenarioPartsPlan): also not form fields.
    const { data: parts = [] } = useEstateParts(open)
    const { data: partOptions } = useEstatePartOptions(open)
    const { data: templates } = useScenarioTemplates(open)
    const [plan, setPlan] = useState<PlanValue>({ parts: [], clouds: [] })
    const [template, setTemplate] = useState<ScenarioTemplateKey | null>(null)
    const [dataResidency, setDataResidency] = useState('')
    const [networkPath, setNetworkPath] = useState('')
    const [planError, setPlanError] = useState<string | null>(null)
    useEffect(() => {
        if (!open) return
        setPlan({ parts: scenario?.plan?.parts ?? [], clouds: (scenario?.plan?.clouds ?? []).map((c) => c.id) })
        setTemplate(scenario?.template ?? null)
        setDataResidency(scenario?.dataResidency ?? '')
        setNetworkPath(scenario?.networkPath ?? '')
        setPlanError(null)
    }, [open, scenario])
    const chooseTemplate = (key: ScenarioTemplateKey) => {
        const chosen = templates?.[key]
        if (!chosen) return
        setTemplate(key)
        setPlan((current) => applyTemplate(chosen, parts, current))
        setPlanError(null)
    }

    useEffect(() => {
        if (open) {
            if (scenario) {
                form.reset({
                    name: scenario.name || '',
                    description: scenario.description || '',
                    useCase: scenario.useCase || '',
                    whenNotToUse: scenario.whenNotToUse || '',
                    isDefault: scenario.isDefault ?? false,
                    isProduction: scenario.isProduction ?? false,
                })
            } else {
                form.reset({
                    name: '',
                    description: '',
                    useCase: '',
                    whenNotToUse: '',
                    isDefault: false,
                    isProduction: false,
                })
            }
        }
    }, [open, scenario, form])

    const isDefault = form.watch('isDefault')
    const isProduction = form.watch('isProduction')

    const onSubmit = async (data: ScenarioFormData) => {
        const problem = planProblem(plan, t as never, (id) => parts.find((p) => p.id === id)?.name ?? `#${id}`)
        setPlanError(problem)
        if (problem) return
        try {
            const submitData = {
                name: data.name.trim(),
                // An empty text is sent as empty, so a field can be cleared (it used to be left as it was).
                description: data.description?.trim() ?? '',
                useCase: data.useCase?.trim() ?? '',
                whenNotToUse: data.whenNotToUse?.trim() ?? '',
                isDefault: data.isDefault,
                isProduction: data.isProduction,
                dependsOn: dependsOn.map(({ kind, refId }) => ({ kind, refId })),
                plan: {
                    parts: plan.parts.map(({ partId, recoversTo, locationId, fallbackTo, fallbackLocationId, connectsTo }) => ({ partId, recoversTo, locationId, fallbackTo, fallbackLocationId, connectsTo })),
                    clouds: plan.clouds,
                },
                dataResidency: dataResidency.trim(),
                networkPath: networkPath.trim(),
                template,
            }

            if (isEditing && scenario) {
                await updateScenario.mutateAsync({ id: scenario.id, data: submitData })
            } else {
                await createScenario.mutateAsync(submitData)
            }
            onOpenChange(false)
        } catch (error) {
            // Error handling is done in the hooks
        }
    }

    const isPending = createScenario.isPending || updateScenario.isPending

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-5xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {isEditing
                            ? t('recoveryScenarios.editScenario', 'Edit Scenario')
                            : t('recoveryScenarios.createScenario', 'Add Scenario')}
                    </DialogTitle>
                    <DialogDescription>
                        {isEditing
                            ? t('recoveryScenarios.editScenarioDescription', 'Edit recovery scenario details')
                            : t('recoveryScenarios.createScenarioDescription', 'Create a new recovery scenario')}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="name">
                            {t('recoveryScenarios.name', 'Name')} <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="name"
                            {...form.register('name')}
                            placeholder={t('recoveryScenarios.namePlaceholder', 'Enter scenario name')}
                        />
                        {form.formState.errors.name && (
                            <p className="text-sm text-destructive">
                                {form.formState.errors.name.message}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="description">{t('recoveryScenarios.description', 'Description')}</Label>
                        <Textarea
                            id="description"
                            {...form.register('description')}
                            placeholder={t('recoveryScenarios.descriptionPlaceholder', 'Enter description')}
                            rows={3}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="useCase">{t('recoveryScenarios.useCase', 'Use Case')}</Label>
                        <Textarea
                            id="useCase"
                            {...form.register('useCase')}
                            placeholder={t('recoveryScenarios.useCasePlaceholder', 'Enter use case')}
                            rows={3}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="whenNotToUse">{t('recoveryScenarios.dontUseWhen', 'Do not use when')}</Label>
                        <Textarea
                            id="whenNotToUse"
                            {...form.register('whenNotToUse')}
                            placeholder={t('recoveryScenarios.dontUseWhenPlaceholder', 'When this scenario is the wrong choice')}
                            rows={2}
                        />
                    </div>

                    <ScenarioPartsPlan
                        value={plan}
                        onChange={(next) => { setPlan(next); setPlanError(null) }}
                        parts={parts}
                        options={partOptions}
                        templates={templates}
                        template={template}
                        onTemplate={chooseTemplate}
                        dataResidency={dataResidency}
                        onDataResidency={setDataResidency}
                        networkPath={networkPath}
                        onNetworkPath={setNetworkPath}
                    />
                    {planError && <p role="alert" className="text-sm text-destructive" data-testid="scenario-plan-error">{planError}</p>}

                    <ScenarioDependsOn value={dependsOn} onChange={setDependsOn} enabled={open} />
                    <div className="space-y-1 rounded-lg border border-sky-600/40 bg-sky-600/5 p-3">
                        <div className="flex items-center space-x-3">
                            <Switch
                                id="isProduction"
                                checked={isEditingCurrentProduction ? true : isProduction}
                                disabled={isEditingCurrentProduction}
                                onCheckedChange={(checked) => { if (!isEditingCurrentProduction) form.setValue('isProduction', checked, { shouldValidate: true }) }}
                            />
                            <Label htmlFor="isProduction" className="cursor-pointer">{t('recoveryScenarios.production', 'Production')}</Label>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {t('recoveryScenarios.productionHelp', 'How the minimum viable company runs today. Every impact assessment measures what is affected against this scenario; the others are ways to recover. It need not be the default.')}
                        </p>
                        {isEditingCurrentProduction && (
                            <p className="text-xs text-muted-foreground">{t('recoveryScenarios.productionLockedHelp', 'To change production, mark another scenario as production.')}</p>
                        )}
                    </div>
                    <div className="space-y-2">
                        <div className="flex items-center space-x-3">
                            <Switch
                                id="isDefault"
                                checked={isEditingCurrentDefault ? true : isDefault}
                                disabled={isEditingCurrentDefault}
                                onCheckedChange={(checked) => {
                                    if (!isEditingCurrentDefault) {
                                        form.setValue('isDefault', checked, { shouldValidate: true })
                                    }
                                }}
                            />
                            <Label htmlFor="isDefault" className="cursor-pointer">
                                {t('recoveryScenarios.defaultScenario', 'Default scenario')}
                            </Label>
                        </div>
                        {isEditingCurrentDefault && (
                            <p className="text-xs text-muted-foreground">
                                {t(
                                    'recoveryScenarios.defaultLockedHelp',
                                    'To change the default, mark another scenario as default.'
                                )}
                            </p>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isPending}
                        >
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button type="submit" disabled={isPending}>
                            {isPending
                                ? t('common.saving', 'Saving...')
                                : isEditing
                                ? t('common.save', 'Save')
                                : t('recoveryScenarios.createScenario', 'Add Scenario')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
